import { Typography } from "@/components/atoms";
import {
  LayoutToggle,
  ProfileNotificationHeader,
  PropertyCard,
  PropertyCardSkeleton,
} from "@/components/molecules";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useContentfulRevision, useI18nContext, useTranslation, useUIColors } from "@/core/hooks";
// @ts-ignore Dynatrace service is ts-nocheck; runtime export is available
import { trackDynatraceEvent } from "@/core/services/dynatraceService";
import { useAppStore } from "@/core/stores/appStore";
import { getUserFirstName } from "@/core/utils";
import { usePropertiesLayout } from "@/modules/home/hooks/usePropertiesLayout";
import { fetchPropertyImagesForProjects } from "@/modules/home/hooks/usePropertyImages";
import {
  calculatePropertyOutstanding,
  getPropertyInstallments,
} from "@/modules/payments/utils/installmentUtils";
import { router } from "expo-router";
import React, { useEffect, useState } from "react";
import {
  ImageSourcePropType,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

/**
 * Map Property from store to PropertyCard props
 */
const mapPropertyToCard = (
  property: ReturnType<typeof useAppStore.getState>["properties"][0],
  unitInstallments: ReturnType<typeof useAppStore.getState>["unitInstallments"],
  t: (key: any, params?: any) => string,
  isRTL: boolean,
) => {
  // Calculate construction completed percentage (based on handover status)
  // Only show if handover is complete
  const constructionCompleted = property.handoverComplete ? "100%" : undefined;

  // Calculate installment paid percentage
  const installmentPaidPercent =
    property.totalPrice > 0
      ? Math.round((property.paidAmount / property.totalPrice) * 100)
      : 0;
  const installmentPaid = `${installmentPaidPercent}%`;

  // Get installments for this property to calculate status
  const installments = getPropertyInstallments(unitInstallments, property.id);
  const outstandingAmount = calculatePropertyOutstanding(installments);

  // Determine variant based on installment data
  let variant: "default" | "upcoming" | "noPending" = "default";
  let statusText = t("properties.noPendingDues");
  let statusCount: number | undefined;
  let statusLabel: string | undefined;

  let upcomingDueCount = 0;
  if (installments) {
    const overdueCount = installments.counts.overdue;
    upcomingDueCount = installments.counts.upcomingDue;

    if (overdueCount > 0) {
      // Has overdue payments - show red status
      variant = "default";
      const missedLabel =
        overdueCount === 1
          ? t("properties.missedPayment")
          : t("properties.missedPayments");
      statusCount = overdueCount;
      statusLabel = missedLabel;
      statusText = isRTL
        ? `${missedLabel} : ${overdueCount}`
        : `${overdueCount} ${missedLabel}`;
    } else if (upcomingDueCount > 0) {
      // Has payments due in 30 days - show yellow status
      variant = "upcoming";
      // Use singular or plural translation key based on count
      const translationKey =
        upcomingDueCount === 1
          ? "properties.paymentDueSoon"
          : "properties.paymentsDueSoon";
      const dueSoonLabel = t(translationKey).replace("{count}", "").trim();
      statusCount = upcomingDueCount;
      statusLabel = dueSoonLabel;
      statusText = isRTL
        ? `${dueSoonLabel} : ${upcomingDueCount}`
        : t(translationKey).replace("{count}", upcomingDueCount.toString());
    } else {
      // No overdue, no upcoming due (only future payments or no payments) - show green status
      variant = "noPending";
      statusText = t("properties.noPendingDues");
    }
  } else if (outstandingAmount > 0) {
    // Fallback to property outstanding amount if installments not available
    if (property.handoverComplete) {
      variant = "default";
      statusText = t("properties.missedPayments");
    } else {
      variant = "upcoming";
      statusText = t("properties.paymentsDueSoon");
    }
  } else {
    // No outstanding amount - show green status
    variant = "noPending";
    statusText = t("properties.noPendingDues");
  }

  // Create unit title from property data
  const unitTitle = property.unitNumber
    ? `${property.unitNumber}${property.bedrooms ? `, ${property.bedrooms}BHK` : ""}`
    : property.projectName;

  return {
    projectName: property.projectName,
    unitTitle,
    constructionCompleted,
    installmentPaid,
    variant,
    statusText,
    statusCount,
    statusLabel,
    images: [], // Will be populated from propertyImagesMap
    adhaUnitStatus:
      property.isADHAUnit && property.adhaUnitStatus?.trim()
        ? property.adhaUnitStatus.trim()
        : undefined,
  };
};

export function PropertiesScreen() {
  // All hooks must be called unconditionally and in the same order every render
  const user = useAppStore((state) => state.auth.user);
  const properties = useAppStore((state) => state.properties);
  const unitInstallments = useAppStore((state) => state.unitInstallments);
  const profileLoading = useAppStore((state) => state.profileLoading);
  const unitInstallmentsLoading = useAppStore(
    (state) => state.unitInstallmentsLoading,
  );
  const colors = useUIColors();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const { layout, setLayout } = usePropertiesLayout();
  const { isRTL } = useI18nContext();
  // Derived values (not hooks) - call after all hooks
  const firstName = getUserFirstName(user);

  // State for property images mapped by project name
  const [propertyImagesMap, setPropertyImagesMap] = useState<
    Record<string, ImageSourcePropType[]>
  >({});
  const [imagesLoading, setImagesLoading] = useState(false);

  // Fetch property images when properties change, and after an app-wide Contentful refresh
  const contentfulRevision = useContentfulRevision();
  useEffect(() => {
    const fetchImages = async () => {
      if (properties.length === 0) return;

      // Get unique project names
      const projectNames = Array.from(
        new Set(properties.map((p) => p.projectName).filter(Boolean)),
      ) as string[];

      if (projectNames.length === 0) return;

      setImagesLoading(true);
      try {
        const imagesMap = await fetchPropertyImagesForProjects(projectNames);
        setPropertyImagesMap(imagesMap);
      } catch (error) {
        // Error already logged in fetchPropertyImagesForProjects
      } finally {
        setImagesLoading(false);
      }
    };

    void fetchImages();
  }, [properties, contentfulRevision]);

  useEffect(() => {
    trackDynatraceEvent({
      p_screen: DT_SCREEN.PROPERTIES,
      p_actionName: "my_properties_viewed",
      eventType: "view",
    });
  }, []);

  return (
    <View
      style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}
    >
      <ScrollView
        contentContainerStyle={[styles.scrollContent]}
        showsVerticalScrollIndicator={false}
        contentInsetAdjustmentBehavior="never"
      >
        {/* Header - positioned at top, scrolls with content */}
        <View style={styles.headerContainer}>
          <ProfileNotificationHeader
            title={t("properties.myProperties")}
            onProfilePress={() => router.push("/profile")}
            onNotificationPress={() => router.push("/notifications")}
            style={{ paddingTop: insets.top + 8 }}
          />
        </View>
        <View style={[styles.contentSection, { paddingHorizontal: 16 }]}>
          <View style={styles.header}>
            <View style={styles.greeting}>
              <Typography
                variant="title2"
                color="primary"
                style={isRTL && { paddingLeft: 4 }}
              >
                {t("properties.hello")} {firstName}
              </Typography>
              <Typography
                variant="body"
                color="primary"
                style={[styles.subtitle, isRTL && { paddingLeft: 4 }]}
              >
                {t("properties.subtitle")}
              </Typography>
            </View>
            <LayoutToggle layout={layout} onLayoutChange={setLayout} />
          </View>

          <View style={styles.propertiesList}>
            {profileLoading ? (
              // Show skeletons for all properties when profile is loading
              Array.from({ length: 3 }).map((_, index) => (
                <PropertyCardSkeleton
                  key={`skeleton-${index}`}
                  layout={layout}
                />
              ))
            ) : properties.length === 0 ? (
              <View style={styles.emptyState}>
                <Typography
                  variant="body"
                  color="tertiary"
                  style={styles.emptyText}
                >
                  {t("properties.noPropertiesFound")}
                </Typography>
              </View>
            ) : (
              properties.map((property) => {
                // Get installments for this property
                const installments = getPropertyInstallments(
                  unitInstallments,
                  property.id,
                );

                // Show skeleton if unit installments are loading and this property doesn't have data yet
                const showSkeleton = unitInstallmentsLoading && !installments;

                if (showSkeleton) {
                  return (
                    <PropertyCardSkeleton key={property.id} layout={layout} />
                  );
                }

                const cardProps = mapPropertyToCard(
                  property,
                  unitInstallments,
                  t,
                  isRTL,
                );

                // Get images for this property from Contentful
                const propertyImages = property.projectName
                  ? propertyImagesMap[property.projectName] || []
                  : [];

                // Collect payment IDs based on variant:
                // - For 'upcoming' variant: only select upcomingDue items (matching the count shown)
                // - For 'default' variant: select overdue items
                // - For 'noPending': no items to select
                const paymentIdsToSelect: string[] = [];
                if (installments) {
                  if (cardProps.variant === "upcoming") {
                    // Only select upcomingDue items (matching the count in statusText)
                    installments.upcomingDue.forEach((item) => {
                      paymentIdsToSelect.push(item.id);
                    });
                  } else if (cardProps.variant === "default") {
                    // Select overdue items
                    installments.overdue.forEach((item) => {
                      paymentIdsToSelect.push(item.id);
                    });
                  }
                  // Note: We don't select future items when clicking "Pay" from property card
                }

                return (
                  <PropertyCard
                    key={property.id}
                    images={
                      propertyImages.length > 0
                        ? propertyImages
                        : cardProps.images
                    }
                    projectName={cardProps.projectName}
                    unitTitle={cardProps.unitTitle}
                    {...(cardProps.constructionCompleted && {
                      constructionCompleted: cardProps.constructionCompleted,
                    })}
                    installmentPaid={cardProps.installmentPaid}
                    variant={cardProps.variant}
                    statusText={cardProps.statusText}
                    {...(cardProps.statusCount != null && {
                      statusCount: cardProps.statusCount,
                    })}
                    {...(cardProps.statusLabel != null && {
                      statusLabel: cardProps.statusLabel,
                    })}
                    layout={layout}
                    adhaUnitStatus={cardProps.adhaUnitStatus}
                    {...(property.isOnlinePaymentEnabled === true
                      ? {
                          onPrimaryActionPress: () => {
                            if (paymentIdsToSelect.length > 0) {
                              router.push({
                                pathname: "/(auth)/property-payments",
                                params: {
                                  tab: "upcoming",
                                  selectedPaymentIds: paymentIdsToSelect.join(","),
                                },
                              } as any);
                            } else {
                              router.push("/(auth)/(home)/payments" as any);
                            }
                          },
                        }
                      : {})}
                    onPress={() => {
                      router.push({
                        pathname: "/(auth)/property-details",
                        params: { propertyId: property.id },
                      } as any);
                    }}
                  />
                );
              })
            )}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 24,
  },
  headerContainer: {
    paddingHorizontal: 0,
  },
  contentSection: {
    paddingTop: 16,
    gap: 16,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 16,
  },
  greeting: {
    flex: 1,
    gap: 8,
  },
  subtitle: {
    lineHeight: 20,
  },
  propertiesList: {
    gap: 16,
  },
  emptyState: {
    paddingVertical: 48,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    textAlign: "center",
  },
});

export default PropertiesScreen;
